import { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { DocumentDetailOverlayProvider } from '@/components/document-overlay/DocumentDetailOverlayContext'
import { TooltipProvider } from '@/components/ui/tooltip'
import type { AgentScope } from '@/features/agent/AgentChatHost'
import { AgentSidebar } from '@/features/agent/AgentSidebar'
import { AgentSidebarProvider, useAgentSidebar } from './AgentSidebarContext'
import { assistantScopeFor } from './assistant-launcher'
import { IconRail } from './IconRail'
import { SectionSidebar } from './SectionSidebar'
import { type Section, sectionHasSidebar } from './sections'
import { ToolbarProvider } from './ToolbarContext'
import { TopBar } from './TopBar'
import { useActiveNav } from './useActiveNav'
import { useLastCaseId } from './useLastCaseId'
import { useNavShellState } from './useNavShellState'

function AgentSidebarMount({
  scope,
  caseId,
  currentView,
}: {
  scope: AgentScope | null
  caseId: string | undefined
  currentView: string | undefined
}) {
  const agent = useAgentSidebar()
  if (!scope || !agent.open) return null
  if (scope === 'case' && !caseId) return null
  return (
    <AgentSidebar
      scope={scope}
      caseId={scope === 'case' ? caseId : undefined}
      currentView={currentView}
      onClose={() => agent.setOpen(false)}
      onGraphAction={agent.dispatchGraphAction}
    />
  )
}

export function AppShell() {
  const navigate = useNavigate()
  const { expanded, setExpanded } = useNavShellState()
  const { pathname, section, caseId, activeWorkspace, activeSubView } = useActiveNav()
  const { effectiveCaseId } = useLastCaseId()
  const [hoveredSection, setHoveredSection] = useState<Section | null>(null)
  const [keepAlive, setKeepAlive] = useState(false)

  const handleSectionClick = (s: Section) => {
    setExpanded(true)
    setHoveredSection(null)
    if (section?.key !== s.key) navigate(s.defaultRoute)
  }

  const pinnedSection = expanded && sectionHasSidebar(section) ? section : undefined
  // Hover-to-peek only applies when the sidebar is collapsed. When expanded,
  // switching sections requires an explicit icon click.
  const rawOverlay = pinnedSection
    ? null
    : hoveredSection
      ? hoveredSection
      : keepAlive
        ? section
        : null
  const overlaySection = sectionHasSidebar(rawOverlay) ? rawOverlay : null

  // Current view string sent to AgentSidebar adapter — mirrors the previous CaseLayout logic.
  const currentView =
    activeWorkspace?.key === 'investigate'
      ? activeSubView?.path || 'investigate-overview'
      : activeWorkspace?.key

  // Sidebar scope is derived from the route: `/cases/{id}/*` → case-scoped
  // agent, `/data` (and future `/data/*` sub-routes) → lake-scoped agent.
  // Everything else suppresses the sidebar entirely.
  const agentScope = assistantScopeFor(pathname, caseId)
  const agentView = agentScope === 'lake' ? 'lake' : currentView

  const shellInner = (
    // IconRail and SectionSidebar hide themselves below md, where the top
    // bar's menu button opens the same navigation as a drawer (MobileNav).
    <div className="relative flex h-dvh w-full overflow-hidden bg-background">
      <IconRail
        activeSection={section?.key}
        onSectionClick={handleSectionClick}
        onSectionHover={s => setHoveredSection(s)}
        caseId={caseId}
        activeSubViewPath={activeSubView?.path}
      />

      {pinnedSection && (
        <SectionSidebar
          section={pinnedSection}
          caseId={effectiveCaseId}
          activeWorkspace={activeWorkspace}
          activeSubView={activeSubView}
          pathname={pathname}
          onCollapse={() => setExpanded(false)}
        />
      )}

      {overlaySection && (
        <div
          onMouseLeave={() => {
            if (!keepAlive) setHoveredSection(null)
          }}
          onMouseEnter={() => setHoveredSection(overlaySection)}
        >
          <SectionSidebar
            section={overlaySection}
            asOverlay
            caseId={effectiveCaseId}
            activeWorkspace={activeWorkspace}
            activeSubView={activeSubView}
            pathname={pathname}
            onPickerOpenChange={open => {
              setKeepAlive(open)
              if (!open) setHoveredSection(null)
            }}
          />
        </div>
      )}

      <main className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </div>
      </main>

      {/* Full-height agent sidebar — sibling of <main>, pushes top bar + content */}
      <AgentSidebarMount scope={agentScope} caseId={caseId} currentView={agentView} />
    </div>
  )

  return (
    <TooltipProvider>
      <AgentSidebarProvider>
        <ToolbarProvider>
          {caseId ? (
            <DocumentDetailOverlayProvider caseId={caseId}>
              {shellInner}
            </DocumentDetailOverlayProvider>
          ) : (
            shellInner
          )}
        </ToolbarProvider>
      </AgentSidebarProvider>
    </TooltipProvider>
  )
}
